import { makeAutoObservable } from "mobx";
import type {
  StoreDiagnosticEvent,
  StoreDiagnosticLevel,
} from "@intrasync/stores";

/**
 * The in-app retention half of HQ's DiagnosticsSink.
 *
 * WHY IT EXISTS: `@intrasync/stores` emits structured diagnostics, but with no
 * sink installed the default delivery level is `warn`, `info` never reaches
 * anything, and `debug`/`info` are deliberately NOT console-mirrored. The
 * reconnect-auth hang that a human eventually reported as "the screen is
 * frozen" was a `diagnostics.warn` that went into no sink at all. Console alone
 * is not enough either — nobody has devtools open when it happens, and the
 * console is gone on the next reload. So warn/error are retained HERE, in the
 * page, where an admin can read them without a devtools session.
 *
 * BOUNDED BY CONSTRUCTION. HQ is a long-lived SPA: a socket that flaps for an
 * hour can emit thousands of `connection.lost` warnings. The buffer is a fixed
 * -capacity ring — it overwrites oldest-first and counts what it dropped, so
 * memory is O(capacity) forever rather than O(uptime).
 *
 * RETAINED EVENTS HOLD NO LIVE REFERENCES. Events arrive already redacted (the
 * library reduces an Error to `{name, message, code}` and scrubs credential
 * -shaped keys), but `defaultRedactDiagnostic` stops cloning at depth 4, so a
 * deep `data` value could still be the store's own object. {@link sanitize}
 * re-serialises `data` through JSON, which both severs those references and
 * caps the retained size. A ring buffer that pins store instances is a leak
 * wearing a leak-prevention costume.
 */

/** A retained event: the redacted library event plus a stable ordering id. */
export interface RetainedDiagnostic extends StoreDiagnosticEvent {
  /** Monotonic across the buffer's lifetime; survives ring wrap-around. */
  seq: number;
}

/** Levels the ring retains. `debug`/`info` are transient by policy. */
const RETAINED_LEVELS: readonly StoreDiagnosticLevel[] = ["warn", "error"];

/** Cap on the serialised size of a single event's `data`, in characters. */
const MAX_DATA_CHARS = 2000;

/**
 * JSON round-trip `data` so the ring cannot pin a store instance, and truncate
 * it so one pathological event cannot dominate the buffer's memory.
 */
function sanitize(event: StoreDiagnosticEvent): StoreDiagnosticEvent {
  if (event.data === undefined) return event;
  let data: Record<string, unknown>;
  try {
    const json = JSON.stringify(event.data);
    if (json === undefined) {
      data = { unserializable: true };
    } else if (json.length > MAX_DATA_CHARS) {
      data = { truncated: true, preview: json.slice(0, MAX_DATA_CHARS) };
    } else {
      data = JSON.parse(json) as Record<string, unknown>;
    }
  } catch {
    // Circular or throwing `toJSON` — keep the event, drop the payload.
    data = { unserializable: true };
  }
  return { ...event, data };
}

export class DiagnosticsBuffer {
  /** Fixed-size backing store; `undefined` until the ring has filled once. */
  private readonly slots: (RetainedDiagnostic | undefined)[];
  private writeIndex = 0;
  private nextSeq = 1;

  /** Number of events evicted by wrap-around. Observable (the panel shows it). */
  droppedCount = 0;
  /**
   * Bumped on every retained write so `events` re-computes under MobX. `slots`
   * is deliberately NOT observable — a 250-slot observable array churns
   * dependencies on every single write — so this counter is the dependency.
   */
  revision = 0;
  /** Per-level totals since load — including events the ring later evicted. */
  totals: Record<StoreDiagnosticLevel, number> = {
    debug: 0,
    info: 0,
    warn: 0,
    error: 0,
  };

  constructor(readonly capacity: number = 250) {
    if (capacity < 1) throw new Error("DiagnosticsBuffer capacity must be >= 1");
    this.slots = new Array<RetainedDiagnostic | undefined>(capacity);
    makeAutoObservable<this, "slots" | "writeIndex" | "nextSeq">(this, {
      slots: false,
      writeIndex: false,
      nextSeq: false,
      capacity: false,
    });
  }

  /** True when this level is retained rather than merely counted. */
  retains(level: StoreDiagnosticLevel): boolean {
    return RETAINED_LEVELS.includes(level);
  }

  /**
   * Record an event. Every level is counted; only `warn`/`error` are retained.
   * Returns the retained record, or `null` when the level is count-only.
   */
  record(event: StoreDiagnosticEvent): RetainedDiagnostic | null {
    this.totals[event.level] = (this.totals[event.level] ?? 0) + 1;
    if (!this.retains(event.level)) return null;

    const retained: RetainedDiagnostic = {
      ...sanitize(event),
      seq: this.nextSeq++,
    };
    if (this.slots[this.writeIndex] !== undefined) this.droppedCount += 1;
    this.slots[this.writeIndex] = retained;
    this.writeIndex = (this.writeIndex + 1) % this.capacity;
    this.revision += 1;
    return retained;
  }

  /** Retained events, oldest first. */
  get events(): RetainedDiagnostic[] {
    void this.revision;
    const out: RetainedDiagnostic[] = [];
    for (let i = 0; i < this.capacity; i += 1) {
      const slot = this.slots[(this.writeIndex + i) % this.capacity];
      if (slot !== undefined) out.push(slot);
    }
    return out;
  }

  get errorCount(): number {
    return this.totals.error;
  }

  get warnCount(): number {
    return this.totals.warn;
  }

  clear(): void {
    this.slots.fill(undefined);
    this.writeIndex = 0;
    this.droppedCount = 0;
    this.revision += 1;
  }
}

/**
 * The one buffer for the page. Created at module scope because the sink is
 * configured in `rootStoreConfig.ts` — i.e. BEFORE any store exists — and the
 * events fired inside the RootStore constructor are exactly the ones worth
 * having.
 */
export const hqDiagnosticsBuffer = new DiagnosticsBuffer(250);

export default hqDiagnosticsBuffer;
